import React, { useMemo } from 'react';

import { isAnnounced, isFundRaising, isPaid, isRefunded, PrimaryMarketProject } from 'api-client';
import { MetricProgressBar, Money } from 'components/common';
import apiClientConfig from 'configs/app';
import { formatDate, formatPercents } from 'formatters';
import { declOfNum, Environment } from 'utils';

const MONTHS = ['месяц', 'месяца', 'месяцев'] as const;

const monthsBetween = (start?: Date | null, end?: Date | null): number | null => {
  if (!start || !end) {
    return null;
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  return (
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth()) -
    (endDate.getDate() < startDate.getDate() ? 1 : 0)
  );
};

const calculateFactTerm = (start?: Date, end?: Date): number | null => {
  const months = monthsBetween(start, end);

  if (months === null) {
    return null;
  }

  return months < 0 ? 0 : months;
};

const calculatePercentage = (current: number, total: number): number => {
  if (total <= 0) {
    return 0;
  }
  return (current / total) * 100;
};

export const usePrimaryMarketProjectContent = (project: PrimaryMarketProject) => {
  const devAndTest =
    apiClientConfig.applicationEnvironment === Environment.dev ||
    apiClientConfig.applicationEnvironment === Environment.test;
  const restOfMoney = project.debtPrice.amount - project.investedSum.amount;
  const canInvest = isFundRaising(project.status) && restOfMoney > 0;
  const showInvestButton = canInvest || isAnnounced(project.status);
  const isFullyCollected = isFundRaising(project.status) && restOfMoney <= 0;

  const roiIrr = useMemo(() => {
    if (isPaid(project.status)) {
      return `${formatPercents(project.factROI ?? 0, 0)} / ${formatPercents(project.factInterestRate ?? 0, 0)}`;
    }

    return `${formatPercents(project.simpleInterestRate, 0)} / ${formatPercents(project.interestRate, 0)}`;
  }, [project]);

  const progressBar = useMemo(() => {
    if (isAnnounced(project.status)) {
      return (
        <MetricProgressBar
          currentValue={<Money money={project.investedSum} compact />}
          totalValue={<Money money={project.debtPrice} compact />}
          currentLabel="Зарезервировано"
          totalLabel="Лимит резервирования"
          percentage={calculatePercentage(project.investedSum.amount, project.debtPrice.amount)}
        />
      );
    }

    if (isFundRaising(project.status)) {
      return (
        <MetricProgressBar
          currentValue={<Money money={project.investedSum} compact />}
          totalValue={<Money money={project.debtPrice} compact />}
          currentLabel="Собрано"
          totalLabel="Сумма сбора"
          percentage={calculatePercentage(project.investedSum.amount, project.debtPrice.amount)}
        />
      );
    }

    if (isRefunded(project.status)) {
      return (
        <MetricProgressBar
          currentValue={<Money money={project.investedSum} compact />}
          totalValue={<Money money={project.debtPrice} compact />}
          currentLabel="Собрано"
          totalLabel="Сумма сбора"
          percentage={calculatePercentage(project.investedSum.amount, project.debtPrice.amount)}
        />
      );
    }

    const forecastAmount = project.recoveryForecast?.amount ?? project.debtPrice.amount;
    const collectedAmount = project.actualPayment?.amount ?? 0;

    return (
      <MetricProgressBar
        currentValue={project.actualPayment ? <Money money={project.actualPayment} compact /> : '—'}
        totalValue={
          project.recoveryForecast ? <Money money={project.recoveryForecast} compact /> : '—'
        }
        currentLabel="Взыскано"
        totalLabel="Прогноз взыскания"
        percentage={calculatePercentage(collectedAmount, forecastAmount)}
      />
    );
  }, [project]);

  const plannedCompletion = useMemo(() => {
    const plannedEnd = project.projectEndDate ?? project.maturityDate;

    if (!plannedEnd) {
      return '—';
    }

    const monthsLeft = monthsBetween(new Date(), plannedEnd);

    return monthsLeft !== null && monthsLeft > 0
      ? `через ${monthsLeft} ${declOfNum(monthsLeft, [...MONTHS])}`
      : formatDate(plannedEnd);
  }, [project]);

  const factTerm = useMemo(() => {
    const months = calculateFactTerm(project.loanIssuedAtDate, project.paidAt);
    return months === null ? '—' : `${months} мес.`;
  }, [project]);

  return {
    devAndTest,
    showInvestButton,
    isFullyCollected,
    roiIrr,
    progressBar,
    factTerm,
    plannedCompletion,
    startedAt: project.loanIssuedAtDate ? formatDate(project.loanIssuedAtDate) : '—',
    completedAt: project.paidAt ? formatDate(project.paidAt) : '—',
    cancelledAt: project.cancelledAt ? formatDate(project.cancelledAt) : '—',
    term: `${project.initialTerm.toFixed(0)} мес.`,
  };
};
